// Projects left sidebar — lists projects grouped by status with create/delete.
// Replaces the hardcoded phase-group sub-nav for the /projects module.
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { I } from "../shared/Icon";
import { api } from "../shared/api/client";
import { toast } from "../shared/stores/toasts";
import { useAuth } from "../shared/stores/auth";
import { ContextMenu, type CtxItem } from "./SubNav";

type Project = {
  id: number;
  code: string | null;
  name: string;
  description: string | null;
  owner_id: number | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  is_private: boolean;
  is_template: boolean;
  member_ids: number[];
  created_at: string;
};

type DirUser = { id: number; name: string };

const STATUS_GROUPS: { id: "active" | "upcoming" | "archived"; title: string; statuses: string[] }[] = [
  { id: "active",   title: "진행 중",      statuses: ["ACTIVE", "PAUSED"] },
  { id: "upcoming", title: "진행 예정",    statuses: ["UPCOMING"] },
  { id: "archived", title: "아카이빙",     statuses: ["ARCHIVED", "COMPLETED"] },
];

export function ProjectsSidebar() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeId = Number(params.get("project")) || null;
  const me = useAuth(s => s.user);

  const [projects, setProjects] = useState<Project[] | null>(null);
  const [showArchived, setShowArchived] = useState<boolean>(() => localStorage.getItem("t-proj-show-archived") === "1");
  const [creating, setCreating] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; items: CtxItem[] } | null>(null);
  const [renaming, setRenaming] = useState<number | null>(null);
  const [memberModal, setMemberModal] = useState<Project | null>(null);

  const reload = () => {
    api.get<Project[]>("/projects")
      .then(r => setProjects(r.data))
      .catch(() => setProjects([]));
  };

  useEffect(() => { reload(); }, []);

  useEffect(() => { localStorage.setItem("t-proj-show-archived", showArchived ? "1" : "0"); }, [showArchived]);

  const selectProject = (id: number) => {
    const np = new URLSearchParams(params);
    np.set("project", String(id));
    setParams(np, { replace: false });
    nav(`/projects?${np.toString()}`);
  };

  const groupedProjects = STATUS_GROUPS.map(g => ({
    ...g,
    projects: (projects ?? []).filter(p => g.statuses.includes(p.status)),
  }));

  const deleteProject = async (p: Project) => {
    if (!window.confirm(`"${p.name}" 프로젝트를 삭제하시겠습니까?\n관련 태스크도 모두 함께 삭제됩니다.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("프로젝트 삭제됨");
      if (activeId === p.id) {
        const np = new URLSearchParams(params);
        np.delete("project");
        setParams(np, { replace: true });
      }
      reload();
    } catch (e: any) {
      toast.error("삭제 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "");
    }
  };

  const renameProject = async (id: number, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) { setRenaming(null); return; }
    try {
      await api.patch(`/projects/${id}`, { name: trimmed });
      reload();
    } catch (e: any) {
      toast.error("이름 변경 실패", e?.response?.data?.detail ?? "");
    }
    setRenaming(null);
  };

  const changeStatus = async (p: Project, status: string) => {
    try {
      await api.patch(`/projects/${p.id}`, { status });
      toast.success("상태 변경됨");
      reload();
    } catch (e: any) {
      toast.error("상태 변경 실패", e?.response?.data?.detail ?? "");
    }
  };

  const toggleTemplate = async (p: Project) => {
    try {
      await api.patch(`/projects/${p.id}`, { is_template: !p.is_template });
      toast.success(p.is_template ? "템플릿 해제됨" : "템플릿으로 지정됨");
      reload();
    } catch (e: any) {
      toast.error("템플릿 변경 실패", e?.response?.data?.detail ?? "");
    }
  };

  const openProjectContext = (e: React.MouseEvent, p: Project) => {
    e.preventDefault();
    const isOwner = me?.id === p.owner_id;
    const items: CtxItem[] = [];
    if (isOwner) {
      items.push({ label: "이름 변경", icon: <I.Edit size={12}/>, onClick: () => setRenaming(p.id) });
      items.push({ label: "참여 인원 관리", icon: <I.Users size={12}/>, onClick: () => setMemberModal(p) });
      items.push({ sep: true, label: "", onClick: () => {} });
      items.push({
        label: p.is_template ? "★ 템플릿 해제" : "☆ 템플릿으로 지정",
        onClick: () => toggleTemplate(p),
      });
      items.push({ sep: true, label: "", onClick: () => {} });
      items.push({ label: "→ 진행 중으로", onClick: () => changeStatus(p, "ACTIVE") });
      items.push({ label: "→ 진행 예정으로", onClick: () => changeStatus(p, "UPCOMING") });
      items.push({ label: "→ 아카이빙", onClick: () => changeStatus(p, "ARCHIVED") });
      items.push({ sep: true, label: "", onClick: () => {} });
      items.push({ label: "삭제", icon: <I.Trash size={12}/>, danger: true, onClick: () => deleteProject(p) });
    } else {
      items.push({ label: "참여 인원 보기", icon: <I.Users size={12}/>, onClick: () => setMemberModal(p) });
    }
    setCtxMenu({ x: e.clientX, y: e.clientY, items });
  };

  return (
    <div className="t-subnav-body" style={{ position: "relative" }}>
      {/* Create button */}
      <button
        className="t-subnav-item"
        style={{
          background: "var(--p-tint, rgba(99,102,241,0.08))",
          color: "var(--p-600, #4f46e5)",
          fontWeight: 600, marginBottom: 12, justifyContent: "center",
          borderLeft: "2px solid transparent",
        }}
        onClick={() => setCreating(true)}
      >
        <I.Plus size={14}/>
        <span>새 프로젝트</span>
      </button>

      {projects === null && (
        <div style={{ padding: 12, fontSize: 12, color: "var(--text-3)" }}>불러오는 중…</div>
      )}

      {projects && projects.length === 0 && (
        <div style={{ padding: 12, fontSize: 12, color: "var(--text-3)", textAlign: "center" }}>
          참여 중인 프로젝트가 없습니다.<br/>위에서 새 프로젝트를 만들어보세요.
        </div>
      )}

      {groupedProjects.map((g) => {
        const isArchivedGroup = g.id === "archived";
        const collapsed = isArchivedGroup && !showArchived;
        if (g.projects.length === 0 && !isArchivedGroup) return null;

        return (
          <div className="t-subnav-group" key={g.id}>
            <div
              className="t-subnav-group-title"
              style={{ display: "flex", alignItems: "center", cursor: isArchivedGroup ? "pointer" : "default" }}
              onClick={() => isArchivedGroup && setShowArchived(v => !v)}
            >
              {isArchivedGroup && (
                <span style={{ marginRight: 4, fontSize: 9 }}>{collapsed ? "▶" : "▼"}</span>
              )}
              <span style={{ flex: 1 }}>{g.title}</span>
              <span style={{ fontSize: 10, color: "var(--text-3)", fontWeight: 500 }}>
                {g.projects.length}
              </span>
            </div>

            {!collapsed && g.projects.map((p) => (
              renaming === p.id ? (
                <div key={p.id} style={{ padding: "2px 8px" }}>
                  <input
                    autoFocus
                    defaultValue={p.name}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") renameProject(p.id, (e.target as HTMLInputElement).value);
                      if (e.key === "Escape") setRenaming(null);
                    }}
                    onBlur={(e) => renameProject(p.id, e.target.value)}
                    style={{
                      width: "100%", fontSize: 14, padding: "4px 8px",
                      border: "1px solid var(--p-400, #818cf8)", borderRadius: 4,
                      outline: "none", background: "var(--bg-0, #fff)",
                    }}
                  />
                </div>
              ) : (
                <button
                  key={p.id}
                  className={`t-subnav-item ${activeId === p.id ? "active" : ""}`}
                  onClick={() => selectProject(p.id)}
                  onContextMenu={(e) => openProjectContext(e, p)}
                  title={p.description || "마우스 오른쪽 클릭 → 메뉴"}
                >
                  <I.Todo size={12}/>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name}
                  </span>
                  {p.is_template && (
                    <span title="템플릿 프로젝트" style={{
                      fontSize: 9, fontWeight: 700, padding: "1px 5px",
                      borderRadius: 3, background: "var(--p-tint, rgba(99,102,241,0.12))",
                      color: "var(--p-700, #4338ca)", letterSpacing: 0.5,
                    }}>T</span>
                  )}
                  {p.is_private && <I.Lock size={10} />}
                </button>
              )
            ))}

            {isArchivedGroup && !collapsed && g.projects.length === 0 && (
              <div style={{ padding: "4px 12px", fontSize: 11, color: "var(--text-3)" }}>없음</div>
            )}
          </div>
        );
      })}

      {creating && (
        <CreateProjectModal
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); reload(); selectProject(id); }}
        />
      )}

      {memberModal && me && (
        <MemberModal
          project={memberModal}
          isOwner={me.id === memberModal.owner_id}
          onClose={() => setMemberModal(null)}
          onChanged={reload}
        />
      )}

      {ctxMenu && (
        <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={ctxMenu.items} onClose={() => setCtxMenu(null)} />
      )}
    </div>
  );
}

// ─── Create project modal ──────────────────────────────────────────────────

function CreateProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<"ACTIVE" | "UPCOMING">("ACTIVE");
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [template, setTemplate] = useState<Project | null>(null);

  useEffect(() => {
    api.get<Project | null>("/projects/template")
      .then(r => setTemplate(r.data))
      .catch(() => setTemplate(null));
  }, []);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const r = await api.post<{ id: number }>("/projects", {
        name: trimmed, description: description.trim() || null,
        status, member_ids: memberIds,
      });
      toast.success("프로젝트 생성됨");
      onCreated(r.data.id);
    } catch (e: any) {
      toast.error("생성 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "");
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="새 프로젝트" onClose={onClose}>
      <div style={{ display: "grid", gap: 12 }}>
        <div>
          <label className="t-label">이름</label>
          <input className="t-input" autoFocus value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && submit()}
            placeholder="프로젝트 이름" />
        </div>
        <div>
          <label className="t-label">설명 (선택)</label>
          <textarea className="t-input" rows={2} style={{ resize: "vertical" }}
            value={description} onChange={e => setDescription(e.target.value)}
            placeholder="프로젝트 설명" />
        </div>
        <div>
          <label className="t-label">상태</label>
          <div style={{ display: "flex", gap: 6 }}>
            {(["ACTIVE", "UPCOMING"] as const).map(s => (
              <button key={s} className={`t-btn ${status === s ? "t-btn-primary" : ""}`}
                onClick={() => setStatus(s)}
                style={{ flex: 1 }}>
                {s === "ACTIVE" ? "진행 중" : "진행 예정"}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="t-label">참여 인원 (선택)</label>
          <MemberPicker selected={memberIds} onChange={setMemberIds} />
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
            ※ 본인은 자동으로 OWNER로 추가됩니다. 추가한 인원만 이 프로젝트를 볼 수 있습니다.
          </div>
        </div>
        {template && (
          <div style={{
            fontSize: 12, padding: "8px 10px", borderRadius: 6,
            background: "var(--p-tint, rgba(99,102,241,0.08))",
            color: "var(--p-700, #4338ca)",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <I.Sparkles size={12}/>
            <span>
              <b>{template.name}</b> 템플릿의 탭 구조가 적용됩니다
            </span>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button className="t-btn" onClick={onClose} disabled={submitting}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={!name.trim() || submitting}>
            {submitting ? "생성 중…" : "생성"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── Member management modal ───────────────────────────────────────────────

function MemberModal({
  project, isOwner, onClose, onChanged,
}: {
  project: Project; isOwner: boolean; onClose: () => void; onChanged: () => void;
}) {
  type Member = { user_id: number; role: string };
  const [members, setMembers] = useState<Member[] | null>(null);
  const [users, setUsers] = useState<Record<number, string>>({});
  const [adding, setAdding] = useState(false);

  const reload = () => {
    api.get<Member[]>(`/projects/${project.id}/members`)
      .then(r => setMembers(r.data))
      .catch(() => setMembers([]));
  };

  useEffect(() => {
    reload();
    api.get<{ data: DirUser[] }>("/users?size=200").then(r => {
      const m: Record<number, string> = {};
      r.data.data.forEach(u => { m[u.id] = u.name; });
      setUsers(m);
    }).catch(() => {});
  }, [project.id]);

  const removeMember = async (uid: number) => {
    if (!window.confirm(`${users[uid] ?? `사용자 #${uid}`} 님을 제외하시겠습니까?`)) return;
    try {
      await api.delete(`/projects/${project.id}/members/${uid}`);
      toast.success("제외됨");
      reload(); onChanged();
    } catch (e: any) {
      toast.error("제외 실패", e?.response?.data?.detail ?? "");
    }
  };

  const addMember = async (uid: number) => {
    try {
      await api.post(`/projects/${project.id}/members`, { user_id: uid, role: "MEMBER" });
      toast.success("추가됨");
      setAdding(false);
      reload(); onChanged();
    } catch (e: any) {
      toast.error("추가 실패", e?.response?.data?.detail ?? "");
    }
  };

  return (
    <ModalShell title={`${project.name} — 참여 인원`} onClose={onClose}>
      {members === null && <div style={{ padding: 12 }}>불러오는 중…</div>}
      {members && (
        <div style={{ display: "grid", gap: 6, maxHeight: 360, overflow: "auto" }}>
          {members.map(m => (
            <div key={m.user_id} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 10px", borderRadius: 6, background: "var(--panel)",
            }}>
              <span style={{ flex: 1, fontSize: 13 }}>
                {users[m.user_id] ?? `사용자 #${m.user_id}`}
              </span>
              <span style={{ fontSize: 11, color: "var(--text-3)" }}>{m.role}</span>
              {isOwner && m.role !== "OWNER" && (
                <button className="t-btn" style={{ padding: "2px 8px", fontSize: 11 }}
                  onClick={() => removeMember(m.user_id)}>제외</button>
              )}
            </div>
          ))}
        </div>
      )}
      {isOwner && (
        <div style={{ marginTop: 12 }}>
          {!adding ? (
            <button className="t-btn t-btn-primary" onClick={() => setAdding(true)}>
              <I.Plus size={12}/> 인원 추가
            </button>
          ) : (
            <MemberPicker
              selected={members?.map(m => m.user_id) ?? []}
              excludeSelected
              onChange={(ids) => {
                const existing = new Set(members?.map(m => m.user_id) ?? []);
                const toAdd = ids.filter(id => !existing.has(id));
                toAdd.forEach(addMember);
                if (toAdd.length === 0) setAdding(false);
              }}
            />
          )}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <button className="t-btn" onClick={onClose}>닫기</button>
      </div>
    </ModalShell>
  );
}

// ─── Member picker (searchable user list) ──────────────────────────────────

export function MemberPicker({
  selected, onChange, excludeSelected = false,
}: {
  selected: number[];
  onChange: (ids: number[]) => void;
  excludeSelected?: boolean;
}) {
  const [users, setUsers] = useState<DirUser[]>([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get<{ data: DirUser[] }>("/users?size=200").then(r => setUsers(r.data.data)).catch(() => {});
  }, []);

  const filtered = users.filter(u => {
    if (q && !u.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (excludeSelected && selected.includes(u.id)) return false;
    return true;
  });

  const toggle = (id: number) => {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  };

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 6 }}>
      <input
        className="t-input"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="이름으로 검색…"
        style={{ marginBottom: 6, fontSize: 12, padding: "4px 8px" }}
      />
      <div style={{ maxHeight: 160, overflow: "auto", display: "grid", gap: 2 }}>
        {filtered.slice(0, 50).map(u => {
          const checked = selected.includes(u.id);
          return (
            <label key={u.id} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "4px 6px", cursor: "pointer", fontSize: 13,
              borderRadius: 4,
              background: checked ? "var(--p-tint, rgba(99,102,241,0.08))" : "transparent",
            }}>
              <input type="checkbox" checked={checked} onChange={() => toggle(u.id)} />
              <span>{u.name}</span>
            </label>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ padding: 8, fontSize: 12, color: "var(--text-3)", textAlign: "center" }}>
            {q ? "검색 결과 없음" : "사용자 없음"}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Modal shell ───────────────────────────────────────────────────────────

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1100,
      background: "rgba(0,0,0,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} style={{
        background: "var(--bg-0, #fff)",
        borderRadius: 10, padding: 20,
        width: "min(480px, 92vw)",
        maxHeight: "85vh", overflow: "auto",
        boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
      }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, flex: 1 }}>{title}</h3>
          <button className="t-btn" onClick={onClose} style={{ padding: "2px 8px" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
